import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaUser,
  FaLock,
  FaSpinner,
  FaEye,
  FaEyeSlash,
  FaPhone,
  FaArrowLeft,
} from "react-icons/fa";
import {
  login,
  requestOtp,
  verifyOtp,
  reset,
  loginSuccess,
} from "../features/auth/authSlice";
import { getFcmToken } from "../firebase/getFcmToken";

const Login = () => {
  const [loginMethod, setLoginMethod] = useState("password"); // "password" or "otp"
  const [currentStep, setCurrentStep] = useState("input"); // "input" or "verify"

  // Password login form data
  const [formData, setFormData] = useState({
    identifier: "", // Can be email or phone
    password: "",
  });

  // OTP login form data
  const [otpData, setOtpData] = useState({
    phone: "",
    otp: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  const { identifier, password } = formData;
  const { phone, otp } = otpData;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { user, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.auth,
  );

  // Get redirect URL from query params
  const queryParams = new URLSearchParams(location.search);
  const redirectUrl = queryParams.get("redirect");

  // Debug logging for redirect URL handling
  console.log("🔍 Login - Current location:", location);
  console.log("🔍 Login - Raw search params:", location.search);
  console.log("🔍 Login - All query params:", Object.fromEntries(queryParams.entries()));
  console.log("🔍 Login - Raw redirect param:", redirectUrl);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(reset());
    }

    if (isSuccess && user) {
      console.log("✅ Login successful, processing redirect...");
      console.log("🔄 Redirect URL from query params:", redirectUrl);

      dispatch(reset());

      // Check for booking intent stored during the booking process
      const bookingIntent = JSON.parse(
        localStorage.getItem("bookingIntent") || "null",
      );

      const storedCab = JSON.parse(
        localStorage.getItem("selectedCabBeforeLogin") || "null",
      );

      if (redirectUrl) {
        // Primary redirect: Use the redirect URL from query params (most reliable)
        console.log("🎯 Using redirect URL from query params");
        console.log("🔍 Raw redirect URL:", redirectUrl);

        const decodedUrl = decodeURIComponent(redirectUrl);
        console.log("🔍 Decoded URL:", decodedUrl);

        // Use navigate with replace option to preserve query parameters
        // Split the URL to ensure proper handling of path and search params
        try {
          const url = new URL(decodedUrl, window.location.origin);
          const pathWithSearch = url.pathname + url.search;
          console.log("🔍 Parsed URL components:");
          console.log("  - pathname:", url.pathname);
          console.log("  - search:", url.search);
          console.log("  - href:", url.href);
          console.log("🔄 Final navigation path:", pathWithSearch);
          navigate(pathWithSearch, { replace: true });
        } catch (urlError) {
          console.error("❌ URL parsing error:", urlError);
          console.log("🔄 Falling back to direct navigation:", decodedUrl);
          // Fallback to direct navigation
          navigate(decodedUrl, { replace: true });
        }
      } else if (bookingIntent && Date.now() - new Date(bookingIntent.timestamp).getTime() < 900000) { // 15 minutes
        // Secondary fallback: Use booking intent (newer method)
        console.log("📋 Using booking intent data:", bookingIntent);
        localStorage.removeItem("bookingIntent");

        const params = new URLSearchParams();
        params.append("from", bookingIntent.from);
        params.append("to", bookingIntent.to);
        params.append("cabId", bookingIntent.cabId);
        params.append("cabName", bookingIntent.cabName);
        params.append("tripType", bookingIntent.type);
        params.append("amount", bookingIntent.amount);
        params.append("distance", bookingIntent.distance);
        params.append("date", bookingIntent.date);
        params.append("time", bookingIntent.time);

        if (bookingIntent.serviceType === "rental" && bookingIntent.packageName) {
          params.append("service", "rental");
          params.append("package", bookingIntent.packageName);
        } else if (bookingIntent.serviceType === "airport") {
          params.append("service", "airport");
          if (bookingIntent.airportCode) params.append("airport", bookingIntent.airportCode);
          if (bookingIntent.transferDirection) params.append("direction", bookingIntent.transferDirection);
        }

        if (bookingIntent.viaCities && bookingIntent.viaCities.length > 0) {
          params.append("via", bookingIntent.viaCities.join(","));
        }

        if (bookingIntent.returnDate) params.append("returnDate", bookingIntent.returnDate);
        if (bookingIntent.returnTime) params.append("returnTime", bookingIntent.returnTime);

        const bookingUrl = `/booking/customer-details?${params.toString()}`;
        console.log("📍 Navigating to booking page:", bookingUrl);
        navigate(bookingUrl);
      } else if (storedCab && Date.now() - storedCab.timestamp < 300000) {
        // Tertiary fallback: Use legacy stored cab data (5 minutes)
        console.log("🗃️ Using legacy stored cab data:", storedCab);
        localStorage.removeItem("selectedCabBeforeLogin");

        const searchParams = new URLSearchParams();
        searchParams.append("cabId", storedCab.id);
        searchParams.append("cabName", storedCab.name);
        searchParams.append("from", storedCab.fromCity);
        searchParams.append("to", storedCab.toCity);
        searchParams.append("fromLocation", storedCab.fromLocation);
        searchParams.append("toLocation", storedCab.toLocation);
        searchParams.append("distance", storedCab.distance);
        searchParams.append("date", storedCab.date);
        searchParams.append("time", storedCab.time);
        const fallbackUrl = `/customer-details?${searchParams.toString()}`;
        console.log("📍 Navigating to fallback URL:", fallbackUrl);
        navigate(fallbackUrl);
      } else {
        // Default: Go to home page
        console.log("No redirect data found, going to home page");
        navigate("/");
      }
    }
  }, [user, isError, isSuccess, message, navigate, dispatch, redirectUrl]);

  useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onOtpChange = (e) => {
    setOtpData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!identifier || !password) {
      toast.error("Please enter both email/phone and password");
      return;
    }

    const isEmail = identifier.includes("@");
    const isPhone = /^[0-9]{10}$/.test(identifier);

    if (!isEmail && !isPhone) {
      toast.error(
        "Please enter a valid email address or 10-digit phone number",
      );
      return;
    }

    // Attempt to get FCM token (graceful fallback if not configured)
    let fcmToken = null;
    try {
      fcmToken = await getFcmToken();
    } catch (tokenErr) {
      console.warn('Failed to get FCM token for password login:', tokenErr);
    }

    const userData = isEmail
      ? { email: identifier, password, fcmToken }
      : { phone: identifier, password, fcmToken };

    dispatch(login(userData));
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    if (!phone) {
      toast.error("Please enter your phone number");
      return;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const result = await dispatch(requestOtp({ phone }));

      if (requestOtp.fulfilled.match(result)) {
        toast.success("OTP sent successfully!");
        setCurrentStep("verify");
      } else {
        toast.error(result.payload || "Failed to send OTP");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      console.error("OTP request error:", error);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }

    if (!/^[0-9]{4}$/.test(otp)) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    try {
      let fcmToken = null;
      try {
        fcmToken = await getFcmToken();
      } catch (tokenErr) {
        console.warn('Failed to get FCM token for OTP login:', tokenErr);
      }
      const result = await dispatch(verifyOtp({ phone, otp, fcmToken }));

      if (verifyOtp.fulfilled.match(result)) {
        toast.success("Login successful!");
      } else {
        toast.error(result.payload || "Invalid OTP");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      console.error("OTP verification error:", error);
    }
  };

  const resetToPhoneInput = () => {
    setCurrentStep("input");
    setOtpData({ phone, otp: "" });
  };

  const switchToPasswordLogin = () => {
    setLoginMethod("password");
    setCurrentStep("input");
    setOtpData({ phone: "", otp: "" });
  };

  const switchToOtpLogin = () => {
    setLoginMethod("otp");
    setCurrentStep("input");
    setFormData({ identifier: "", password: "" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

          {/* Left: Hero */}
          <div className="login-hero p-6 lg:p-12">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Welcome back to UrbanRide</h2>
            <p className="text-gray-600 mb-6">Sign in to manage bookings, view your rides, and access exclusive deals.</p>

            <div className="hidden md:block bg-white rounded-lg shadow-sm p-6">
              <svg className="w-full h-48 object-cover" viewBox="0 0 600 200" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                <rect width="600" height="200" fill="#eef2ff" />
                <g fill="#c7d2fe">
                  <circle cx="80" cy="80" r="40" />
                  <rect x="160" y="40" width="120" height="80" rx="12" />
                  <circle cx="420" cy="100" r="50" />
                </g>
              </svg>
            </div>

            <ul className="mt-6 space-y-3 text-sm text-gray-700">
              <li className="flex items-start"><span className="inline-block mr-3 mt-1 text-blue-600">●</span> Secure sign-in with email or phone</li>
              <li className="flex items-start"><span className="inline-block mr-3 mt-1 text-blue-600">●</span> One-tap OTP option for quick access</li>
              <li className="flex items-start"><span className="inline-block mr-3 mt-1 text-blue-600">●</span> Manage bookings and payments from your dashboard</li>
            </ul>
          </div>

          {/* Right: Login Card */}
          <div className="login-card bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-2xl font-bold text-center mb-4">Sign in</h1>

            <div className="mb-6">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={switchToPasswordLogin}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    loginMethod === "password" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  Email / Phone
                </button>
                <button
                  type="button"
                  onClick={switchToOtpLogin}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    loginMethod === "otp" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  OTP
                </button>
              </div>
            </div>

            {loginMethod === "password" && (
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">Email or Phone</label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 text-gray-400"><FaUser /></div>
                    <input
                      type="text"
                      id="identifier"
                      name="identifier"
                      value={identifier}
                      onChange={onChange}
                      className="input-field w-full pl-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your email or phone number"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <div className="absolute left-3 top-3 text-gray-400"><FaLock /></div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={password}
                      onChange={onChange}
                      className="input-field w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your password"
                      required
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 focus:outline-none">{showPassword ? <FaEyeSlash /> : <FaEye />}</button>
                  </div>

                  <div className="mt-2 text-right">
                    <Link to="/user/forgot-password/send-otp" className="text-sm text-blue-600 hover:underline">Forgot password?</Link>
                  </div>
                </div>

                <button type="submit" className={`primary-btn w-full py-3 rounded-lg transition ${isLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} disabled={isLoading}>
                  {isLoading ? <span className="flex items-center justify-center"><FaSpinner className="animate-spin mr-2" />Loading...</span> : 'Sign in'}
                </button>

              </form>
            )}

            {loginMethod === "otp" && (
              <>
                {currentStep === "input" && (
                  <form onSubmit={handleRequestOtp} className="space-y-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone number</label>
                      <div className="relative">
                        <div className="absolute left-3 top-3 text-gray-400"><FaPhone /></div>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={phone}
                          onChange={onOtpChange}
                          className="input-field w-full pl-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter your 10-digit phone number"
                          maxLength="10"
                          required
                        />
                      </div>
                    </div>

                    <button type="submit" className={`primary-btn w-full py-3 rounded-lg transition ${isLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} disabled={isLoading}>
                      {isLoading ? <span className="flex items-center justify-center"><FaSpinner className="animate-spin mr-2" />Sending OTP...</span> : 'Send OTP'}
                    </button>
                  </form>
                )}

                {currentStep === "verify" && (
                  <div>
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700">OTP sent to <strong>{phone}</strong></p>
                    </div>

                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                      <div>
                        <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">Enter OTP</label>
                        <input
                          type="text"
                          id="otp"
                          name="otp"
                          value={otp}
                          onChange={onOtpChange}
                          className="input-field w-full text-center text-2xl tracking-widest py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0000"
                          maxLength="4"
                          required
                        />
                      </div>

                      <button type="submit" className={`primary-btn w-full py-3 rounded-lg transition ${isLoading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} disabled={isLoading}>
                        {isLoading ? <span className="flex items-center justify-center"><FaSpinner className="animate-spin mr-2" />Verifying...</span> : 'Verify OTP'}
                      </button>

                      <button type="button" onClick={resetToPhoneInput} className="w-full flex items-center justify-center py-2 text-blue-600 hover:text-blue-800 transition-colors"><FaArrowLeft className="mr-2" />Change Phone Number</button>
                    </form>
                  </div>
                )}
              </>
            )}

            <div className="mt-6 text-center">
              <p className="text-gray-700">Don't have an account? <Link to="/register" className="text-blue-600 font-medium hover:underline">Register</Link></p>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-center font-medium text-gray-700 mb-3">Are you a driver or vendor?</p>
              <div className="space-y-2">
                <Link to="/driver/login" className="block w-full text-center py-2 px-4 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors">Go to Driver Login</Link>
                <Link to="/vendor/login" className="block w-full text-center py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">Go to Vendor Login</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
